import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Bell, CheckCheck, Filter, ChevronRight, Search, Video, List, Table as TableIcon, Trash2, MailOpen, MailWarning } from "lucide-react";
import { fmtRelative } from "@/lib/format";
import { toast } from "sonner";
import AdvancedTable from "@/components/table/AdvancedTable";
import { apiErrorMessage } from "@/lib/apiError";

export default function Notifications() {
  const nav = useNavigate();
  const PAGE_SIZE = 50;
  const [data, setData] = useState({ items: [], unread: 0, total: 0, has_more: false });
  const [filter, setFilter] = useState("all"); // all | unread
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [view, setView] = useState("list"); // list | table
  // Iter 145 — bulk actions.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkPending, setBulkPending] = useState(null); // {action, count} for confirm dialog

  const load = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.get(`/notifications?limit=${PAGE_SIZE}&offset=0`);
      setData({ items: d.items || [], unread: d.unread || 0, total: d.total || 0, has_more: !!d.has_more });
    } finally { setBusy(false); }
  };
  const loadMore = async () => {
    if (loadingMore || !data.has_more) return;
    setLoadingMore(true);
    try {
      const { data: d } = await api.get(`/notifications?limit=${PAGE_SIZE}&offset=${data.items.length}`);
      setData((prev) => ({
        items: [...prev.items, ...(d.items || [])],
        unread: d.unread ?? prev.unread,
        total: d.total ?? prev.total,
        has_more: !!d.has_more,
      }));
    } finally { setLoadingMore(false); }
  };
  useEffect(() => { load(); }, []);

  const types = useMemo(() => {
    const s = new Set();
    (data.items || []).forEach((n) => n.type && s.add(n.type));
    return Array.from(s).sort();
  }, [data.items]);

  const filtered = useMemo(() => {
    let rows = data.items || [];
    if (filter === "unread") rows = rows.filter((n) => !n.read);
    if (type) rows = rows.filter((n) => n.type === type);
    if (q) {
      const t = q.toLowerCase();
      rows = rows.filter((n) => (n.title || "").toLowerCase().includes(t) || (n.body || "").toLowerCase().includes(t));
    }
    return rows;
  }, [data.items, filter, type, q]);

  const groups = useMemo(() => groupByDate(filtered), [filtered]);

  const markAllRead = async () => {
    await api.post("/notifications/mark-read");
    toast.success("All notifications marked read");
    load();
  };

  const open = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`);
    if (n.link) nav(n.link);
    load();
  };

  const runBulk = useCallback(async (action) => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    try {
      const { data: d } = await api.post("/notifications/bulk", { action, ids });
      toast.success(`${action.replace("_", " ")} · ${d.affected || 0} notification${d.affected === 1 ? "" : "s"}`);
      setSelectedIds(new Set());
      setBulkPending(null);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Bulk action failed"));
    }
  }, [selectedIds]);

  const columns = useMemo(() => [
    {
      key: "title", label: "Title", width: 320, sortable: true, filterType: "text",
      accessor: (n) => n.title || "",
      render: (n) => (
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" title="Unread" />}
            <span className="font-medium truncate">{n.title}</span>
          </div>
          {n.body && <div className="text-[11px] text-muted-foreground truncate">{n.body}</div>}
        </div>
      ),
    },
    {
      key: "type", label: "Type", width: 140, sortable: true, filterType: "enum",
      filterOptions: types,
      accessor: (n) => n.type || "",
      render: (n) => n.type ? <span className="chip text-[10px]">{n.type.replace(/_/g, " ")}</span> : null,
    },
    {
      key: "read", label: "State", width: 90, sortable: true, filterType: "enum",
      filterOptions: ["read", "unread"],
      accessor: (n) => n.read ? "read" : "unread",
      render: (n) => n.read
        ? <span className="chip text-[10px]">Read</span>
        : <span className="chip text-[10px] chip-warning">Unread</span>,
    },
    {
      key: "created_at", label: "Received", width: 140, sortable: true, filterType: "date-range",
      accessor: (n) => n.created_at || "",
      render: (n) => <span className="text-[11px] text-muted-foreground">{fmtRelative(n.created_at)}</span>,
    },
  ], [types]);

  const selectedRows = useMemo(() =>
    filtered.filter((n) => selectedIds.has(n.id)),
    [filtered, selectedIds]);

  const hasUnread = selectedRows.some((n) => !n.read);
  const hasRead = selectedRows.some((n) => n.read);

  return (
    <>
      <PageHeader
        eyebrow="Inbox"
        title="Notifications"
        description="Everything that pinged you — comments, approvals, meetings, action items, mentions."
        testid="notifications-header"
        actions={
          <div className="flex items-center gap-2">
            <div className="border border-border inline-flex" data-testid="notif-view-toggle">
              <button
                type="button"
                onClick={() => setView("list")}
                className={`px-2.5 py-1.5 text-[11px] inline-flex items-center gap-1 ${view === "list" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                data-testid="notif-view-list"
              ><List className="w-3 h-3" /> List</button>
              <button
                type="button"
                onClick={() => setView("table")}
                className={`px-2.5 py-1.5 text-[11px] inline-flex items-center gap-1 border-l border-border ${view === "table" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                data-testid="notif-view-table"
              ><TableIcon className="w-3 h-3" /> Table</button>
            </div>
            <Button
              variant={data.unread > 0 ? "default" : "outline"}
              className="rounded-none"
              onClick={markAllRead}
              disabled={data.unread === 0}
              data-testid="notif-mark-all-btn"
            >
              <CheckCheck className="w-3.5 h-3.5 mr-1.5" /> Mark all read{data.unread > 0 ? ` (${data.unread})` : ""}
            </Button>
          </div>
        }
      />
      <div className="px-4 sm:px-6 md:px-10">
        <div className="border border-border bg-muted/40 p-3 flex flex-wrap items-center gap-2" data-testid="notif-filters">
          <Filter className="w-3.5 h-3.5 text-muted-foreground" />
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="notif-filter-state"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="unread">Unread only</SelectItem>
            </SelectContent>
          </Select>
          {types.length > 0 && (
            <Select value={type || "_all"} onValueChange={(v) => setType(v === "_all" ? "" : v)}>
              <SelectTrigger className="h-8 w-44 text-xs" data-testid="notif-filter-type"><SelectValue placeholder="All types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All types</SelectItem>
                {types.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <div className="inline-flex items-center gap-1 ml-2">
            <Search className="w-3 h-3 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="h-8 w-60 text-xs" data-testid="notif-filter-q" />
          </div>
          <span className="text-[10px] text-muted-foreground ml-auto" data-testid="notif-count-info">
            {filtered.length} of {data.items.length}{data.total > data.items.length ? ` · ${data.total} total` : ""}
          </span>
        </div>
      </div>

      <div className="px-4 sm:px-6 md:px-10 py-6">
        {view === "table" ? (
          <div data-testid="notif-table-view">
            <AdvancedTable
              tableKey="notifications-archive"
              title="Notifications"
              rows={filtered}
              columns={columns}
              loading={busy && data.items.length === 0}
              emptyIcon={Bell}
              emptyTitle="No notifications match"
              emptyDescription="Try changing filters or you're all caught up."
              onRowClick={(n) => open(n)}
              selectable
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              renderBulkBar={(rows) => (
                <div className="flex items-center gap-2 flex-wrap" data-testid="notif-bulk-bar">
                  <span className="text-xs">{rows.length} selected</span>
                  <Button
                    size="sm" variant="outline" className="rounded-none h-8"
                    disabled={!hasUnread}
                    onClick={() => setBulkPending({ action: "mark_read", count: rows.length })}
                    data-testid="notif-bulk-mark-read"
                  ><MailOpen className="w-3.5 h-3.5 mr-1" /> Mark read</Button>
                  <Button
                    size="sm" variant="outline" className="rounded-none h-8"
                    disabled={!hasRead}
                    onClick={() => setBulkPending({ action: "mark_unread", count: rows.length })}
                    data-testid="notif-bulk-mark-unread"
                  ><MailWarning className="w-3.5 h-3.5 mr-1" /> Mark unread</Button>
                  <Button
                    size="sm" variant="destructive" className="rounded-none h-8"
                    onClick={() => setBulkPending({ action: "delete", count: rows.length })}
                    data-testid="notif-bulk-delete"
                  ><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</Button>
                </div>
              )}
            />
            {data.has_more && (
              <div className="text-center pt-3">
                <Button
                  variant="outline" className="rounded-none" onClick={loadMore}
                  disabled={loadingMore} data-testid="notif-load-more-btn-table"
                >{loadingMore ? "Loading…" : `Load more (${data.total - data.items.length} left)`}</Button>
              </div>
            )}
          </div>
        ) : busy && data.items.length === 0 ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState title="No notifications match" description="Try changing filters or you're all caught up." icon={Bell} testid="notif-empty" />
        ) : (
          <div className="space-y-6" data-testid="notif-list">
            {groups.map((g) => (
              <section key={g.label}>
                <div className="label-eyebrow mb-2">{g.label} · {g.items.length}</div>
                <div className="border border-border divide-y divide-border">
                  {g.items.map((n) => (
                    <button
                      key={n.id}
                      onClick={() => open(n)}
                      className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-muted/40 transition ${!n.read ? "bg-primary/5" : ""}`}
                      data-testid={`notif-row-${n.id}`}
                    >
                      {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{n.title}</div>
                        {n.body && <div className="text-xs text-muted-foreground line-clamp-2">{n.body}</div>}
                        <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-2">
                          <span>{fmtRelative(n.created_at)}</span>
                          {n.type && <span className="chip text-[9px]">{n.type.replace(/_/g, " ")}</span>}
                        </div>
                      </div>
                      {n.meta?.meeting_link && (
                        <span
                          role="button"
                          onClick={(e) => { e.stopPropagation(); window.open(n.meta.meeting_link, "_blank", "noopener"); }}
                          className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 bg-primary text-primary-foreground hover:opacity-90 mt-0.5"
                          data-testid={`notif-join-${n.id}`}
                        >
                          <Video className="w-3 h-3" /> Join
                        </span>
                      )}
                      {n.link && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground mt-1 shrink-0" />}
                    </button>
                  ))}
                </div>
              </section>
            ))}
            {data.has_more && (
              <div className="text-center pt-2">
                <Button
                  variant="outline"
                  className="rounded-none"
                  onClick={loadMore}
                  disabled={loadingMore}
                  data-testid="notif-load-more-btn"
                >
                  {loadingMore ? "Loading…" : `Load more (${data.total - data.items.length} left)`}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bulk action confirm dialog */}
      <AlertDialog open={!!bulkPending} onOpenChange={(o) => { if (!o) setBulkPending(null); }}>
        <AlertDialogContent data-testid="notif-bulk-confirm">
          {bulkPending && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {bulkPending.action === "delete" ? "Delete notifications?" :
                    bulkPending.action === "mark_read" ? "Mark as read?" : "Mark as unread?"}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {bulkPending.action === "delete"
                    ? `Permanently delete ${bulkPending.count} notification${bulkPending.count === 1 ? "" : "s"}. This action cannot be undone.`
                    : bulkPending.action === "mark_read"
                      ? `Mark ${bulkPending.count} notification${bulkPending.count === 1 ? "" : "s"} as read.`
                      : `Mark ${bulkPending.count} notification${bulkPending.count === 1 ? "" : "s"} as unread.`}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="notif-bulk-confirm-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => runBulk(bulkPending.action)}
                  className={bulkPending.action === "delete" ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
                  data-testid="notif-bulk-confirm-ok"
                >
                  {bulkPending.action === "delete" ? "Delete" : "Confirm"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function groupByDate(items) {
  const today = new Date();
  const ymd = (d) => d.toISOString().slice(0, 10);
  const todayKey = ymd(today);
  const yKey = ymd(new Date(today.getTime() - 86400_000));
  const groups = new Map();
  for (const n of items) {
    let d;
    try { d = new Date(n.created_at); } catch { d = today; }
    const key = ymd(d);
    let label;
    if (key === todayKey) label = "Today";
    else if (key === yKey) label = "Yesterday";
    else {
      const diff = Math.floor((today - d) / 86400_000);
      if (diff < 7) label = "Earlier this week";
      else if (diff < 30) label = "Earlier this month";
      else label = "Older";
    }
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(n);
  }
  const order = ["Today", "Yesterday", "Earlier this week", "Earlier this month", "Older"];
  return order.filter((l) => groups.has(l)).map((l) => ({ label: l, items: groups.get(l) }));
}
